import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, Download, MapPin, Calendar, Clock } from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import CelebrationLasers from "@/components/common/CelebrationLasers";

const PaymentSuccess = () => {
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const qrRef = useRef(null);
  
  const sessionId = searchParams.get('session_id');
  const eventId = searchParams.get('event_id');
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        setError('Sesión de pago no encontrada');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setReservations(response.data.reservations || []);
          
          // Fetch event info
          if (eventId) {
            const eventResponse = await axios.get(`${API}/events`);
            const foundEvent = eventResponse.data.find(e => e.id === eventId);
            if (foundEvent) setEvent(foundEvent);
          }
        } else if (response.data.status === 'expired' || response.data.status === 'cancelled') {
          setStatus('error');
          setError('El pago fue cancelado o expiró');
        } else if (pollingCount < 5) {
          // Keep polling
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('No se pudo verificar el pago. Por favor, contacta con soporte.');
        }
      } catch (e) {
        console.error('Error checking payment:', e);
        if (pollingCount < 5) {
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('Error al verificar el estado del pago');
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, eventId, pollingCount]);

  const downloadQR = async () => {
    if (!qrRef.current || !reservations.length) return;
    
    const allSvgs = qrRef.current.querySelectorAll("svg");
    if (!allSvgs.length) return;

    const numTickets = reservations.length;
    const qrSize = 180;
    const padding = 30;
    const headerHeight = 200;
    const ticketHeight = qrSize + 80;
    const ticketsPerRow = Math.min(numTickets, 3);
    const rows = Math.ceil(numTickets / ticketsPerRow);
    
    const canvasWidth = Math.max(500, ticketsPerRow * (qrSize + padding) + padding * 2);
    const canvasHeight = headerHeight + rows * ticketHeight + 100;
    
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    ctx.fillStyle = "#ec00e8";
    ctx.fillRect(0, 0, canvasWidth, 5);
    
    ctx.fillStyle = "#ec00e8";
    ctx.font = "bold 28px Arial";
    ctx.textAlign = "center";
    ctx.fillText("TAYTANTOS", canvasWidth / 2, 45);
    
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "12px Arial";
    ctx.fillText("¡¡SIENTE LA MÚSICA!!", canvasWidth / 2, 65);
    
    if (event) {
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 18px Arial";
      ctx.fillText(event.nombre, canvasWidth / 2, 100);
      
      ctx.fillStyle = "#00f3ff";
      ctx.font = "14px Arial";
      ctx.fillText(`${event.fecha} - ${event.hora}`, canvasWidth / 2, 125);
      
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "12px Arial";
      ctx.fillText(event.sala, canvasWidth / 2, 145);
    }
    
    ctx.fillStyle = "#ec00e8";
    ctx.font = "bold 14px Arial";
    ctx.fillText(`${reservations[0]?.nombre || ''} - ${numTickets} ${numTickets > 1 ? 'entradas' : 'entrada'}`, canvasWidth / 2, 190);
    
    const qrPromises = Array.from(allSvgs).map((svg, index) => {
      return new Promise((resolve) => {
        const svgData = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        img.onload = () => {
          const row = Math.floor(index / ticketsPerRow);
          const col = index % ticketsPerRow;
          const totalWidthForRow = Math.min(numTickets - row * ticketsPerRow, ticketsPerRow) * (qrSize + padding) - padding;
          const startX = (canvasWidth - totalWidthForRow) / 2;
          const x = startX + col * (qrSize + padding);
          const y = headerHeight + row * ticketHeight;
          
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(x, y, qrSize, qrSize);
          
          ctx.drawImage(img, x, y, qrSize, qrSize);
          
          ctx.fillStyle = "#ec00e8";
          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.fillText(`Entrada ${index + 1}`, x + qrSize / 2, y + qrSize + 20);
          
          ctx.fillStyle = "#71717a";
          ctx.font = "10px Arial";
          ctx.fillText(`ID: ${reservations[index].id.slice(0, 8).toUpperCase()}`, x + qrSize / 2, y + qrSize + 40);
          
          resolve();
        };
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
      });
    });
    
    await Promise.all(qrPromises);
    
    ctx.fillStyle = "#71717a";
    ctx.font = "10px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Presenta este código QR en la entrada del evento", canvasWidth / 2, canvasHeight - 30);
    
    const dataUrl = canvas.toDataURL("image/png");
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>Entradas TAYTANTOS</title></head>
            <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;">
              <div style="text-align:center;">
                <p style="color:#fff;margin-bottom:20px;font-family:sans-serif;">Mantén pulsada la imagen para guardarla</p>
                <img src="${dataUrl}" alt="Entradas TAYTANTOS" style="max-width:95vw;"/>
              </div>
            </body>
          </html>
        `);
      }
    } else {
      const link = document.createElement("a");
      link.download = `entradas-taytantos-${reservations[0]?.id?.slice(0, 8) || 'tickets'}.png`;
      link.href = dataUrl;
      link.click();
    }
  };
  
  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10">
        <div className="text-center mb-6 fade-in-up">
          <h1 className="font-logo text-3xl md:text-4xl text-white neon-glow">
            TAYTANTOS
          </h1>
        </div>
        
        {status === 'checking' && (
          <div className="form-section text-center fade-in-up">
            <div className="spinner mx-auto mb-4" style={{ width: 48, height: 48 }}></div>
            <h2 className="text-white text-xl mb-2">Verificando pago...</h2>
            <p className="text-[var(--text-muted)]">Por favor, espera mientras confirmamos tu pago.</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="form-section text-center fade-in-up">
            <XCircle size={48} className="text-[var(--error)] mx-auto mb-4" />
            <h2 className="text-[var(--error)] text-xl mb-2">Error en el pago</h2>
            <p className="text-[var(--text-muted)] mb-6">{error}</p>
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver a Inicio
            </button>
          </div>
        )}
        
        {status === 'success' && (
          <div className="form-section success-section fade-in-up" data-testid="payment-success-section">
            <div className="w-full p-4 bg-gradient-to-r from-[var(--primary)]/20 via-[var(--primary)]/30 to-[var(--primary)]/20 border-y-2 border-[var(--primary)] mb-6">
              <p className="text-center text-[var(--primary)] font-bold text-xl uppercase tracking-widest">
                ¡Pago Completado!
              </p>
            </div>

            <CheckCircle size={48} className="text-[var(--success)] mx-auto mb-4" />
            <h3 className="success-title">
              {reservations.length > 1 ? `¡${reservations.length} Entradas Compradas!` : '¡Entrada Comprada!'}
            </h3>
            <p className="success-message">
              Te hemos enviado {reservations.length > 1 ? 'los códigos QR' : 'el código QR'} a tu email.
            </p>

            {event && (
              <div className="event-info-box mt-6 mb-6">
                <h4 className="text-white font-bold mb-2">{event.nombre}</h4>
                <p className="text-[var(--text-muted)] text-sm">{event.fecha} - {event.hora}</p>
                <p className="text-[var(--text-muted)] text-sm">{event.sala}</p>
              </div>
            )}

            {reservations.length > 0 && (
              <div className="qr-container mt-6" ref={qrRef}>
                <div className={`grid gap-4 ${reservations.length > 1 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1'} justify-items-center`}>
                  {reservations.map((res, index) => (
                    <div key={res.id} className="qr-wrapper text-center">
                      <div className="qr-code mb-2">
                        <QRCodeSVG 
                          value={`TAYTANTOS-${res.id}`}
                          size={120}
                          bgColor="#ffffff"
                          fgColor="#000000"
                          level="M"
                        />
                      </div>
                      <p className="text-[var(--primary)] text-xs font-bold">
                        Entrada {index + 1}
                      </p>
                      <p className="text-[var(--text-muted)] text-xs">
                        ID: {res.id.slice(0, 8).toUpperCase()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={downloadQR}
                className="btn-download"
                data-testid="download-qr-button"
              >
                <Download size={18} className="inline mr-2" />
                Descargar {reservations.length > 1 ? 'Entradas' : 'Entrada'}
              </button>
              
              <button 
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Volver a Inicio
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Mesa Payment Success Page

export default PaymentSuccess;
